import React from 'react';

import * as am4core from '@amcharts/amcharts4/core';
import am4themes_animated from '@amcharts/amcharts4/themes/animated'; // eslint-disable-line camelcase
import Typography from '@mui/material/Typography';

import type { WebChartRequest } from '../llmWebTools';
import AreaChartPreview from './AreaChartPreview';
import { normalizeChartRequest } from './chartPreviewUtils';
import ColumnChartPreview from './ColumnChartPreview';
import LineChartPreview from './LineChartPreview';
import PieChartPreview from './PieChartPreview';
import SidewaysBarChartPreview from './SidewaysBarChartPreview';

let themeApplied = false;

const ensureAmChartTheme = () => {
  if (!themeApplied) {
    am4core.useTheme(am4themes_animated);
    themeApplied = true;
  }
};

type Props = {
  chart: WebChartRequest;
  height?: number | string;
  width?: number | string;
};

const ChartPreview = ({ chart, height, width }: Props): JSX.Element => {
  ensureAmChartTheme();
  const normalizedChart = React.useMemo(() => normalizeChartRequest(chart), [chart]);
  const computedHeight = React.useMemo(() => {
    if (typeof height !== 'undefined') {
      return height;
    }
    const rowCount = normalizedChart.data?.length ?? 0;
    const minHeight = 240;
    const maxHeight = 520;
    const nextHeight = 120 + rowCount * 28;
    return Math.max(minHeight, Math.min(maxHeight, nextHeight));
  }, [height, normalizedChart.data]);

  if (!normalizedChart.categoryField || !normalizedChart.valueFields?.length || !normalizedChart.data?.length) {
    return (
      <Typography variant='captionRegular' color='textSecondary'>
        Chart preview unavailable (missing fields or data).
      </Typography>
    );
  }

  switch (normalizedChart.chartType) {
    case 'pie':
      return <PieChartPreview chart={normalizedChart} height={computedHeight} width={width} />;
    case 'bar':
    case 'sidewaysBar':
      return <SidewaysBarChartPreview chart={normalizedChart} height={computedHeight} width={width} />;
    case 'column':
      return <ColumnChartPreview chart={normalizedChart} height={computedHeight} width={width} />;
    case 'line':
      return <LineChartPreview chart={normalizedChart} height={computedHeight} width={width} />;
    case 'area':
      return <AreaChartPreview chart={normalizedChart} height={computedHeight} width={width} />;
    default:
      return (
        <Typography variant='captionRegular' color='textSecondary'>
          Chart preview unavailable (unsupported chart type).
        </Typography>
      );
  }
};

export default ChartPreview;
